import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { isMeasurementStarted, Measurement, MEASUREMENT_TYPE_DISPLAY_CONFIG, STAGE_OPEN_GESTURE } from '@cosmos/shared';
import { TranslatePipe } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { MeasurementLivenessBadgeComponent } from '../../components/measurement-liveness-badge/measurement-liveness-badge.component';

type RowStatus = 'started' | 'awaiting-start' | 'never-started' | 'finished';

/**
 * Compact list-view row for a measurement inside the stage browser's list
 * mode — same data as MeasurementCardComponent (status tag, liveness,
 * last-seen, device, primary value) laid out horizontally. Opens on
 * single/double click per STAGE_OPEN_GESTURE, no kebab menu (v1).
 */
@Component({
  selector: 'cosmos-measurement-row',
  imports: [TranslatePipe, TagModule, DatePipe, DecimalPipe, MeasurementLivenessBadgeComponent],
  templateUrl: './measurement-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementRowComponent {
  readonly measurement = input.required<Measurement>();
  readonly selected = input<boolean>(false);
  /** Whether this is the last row in the list (drop the bottom rule). */
  readonly isLast = input<boolean>(false);
  /** Absolute first row in the card — round the selected bg top. */
  readonly isFirst = input<boolean>(false);
  /** Absolute last row in the card — round the selected bg bottom. */
  readonly isAbsoluteLast = input<boolean>(false);

  readonly open = output<void>();
  readonly select = output<void>();

  protected openGesture: 'single' | 'double' = STAGE_OPEN_GESTURE;

  protected readonly status = computed<RowStatus>(() => {
    const m = this.measurement();
    if (isMeasurementStarted(m)) return m.active ? 'started' : 'finished';
    return m.active ? 'awaiting-start' : 'never-started';
  });

  protected readonly statusSeverity = computed(() => {
    switch (this.status()) {
      case 'started': return 'success';
      case 'awaiting-start': return 'warn';
      case 'never-started': return 'secondary';
      case 'finished': return 'info';
    }
  });

  protected readonly displayConfig = computed(() => {
    const m = this.measurement();
    return m.measurementType ? MEASUREMENT_TYPE_DISPLAY_CONFIG[m.measurementType] ?? null : null;
  });

  protected readonly primaryValue = computed(() => {
    const m = this.measurement();
    const config = this.displayConfig();
    if (!config || this.status() === 'awaiting-start' || this.status() === 'never-started') return null;
    const series = (m as any)[config.primaryVariable] as number[] | undefined;
    return series?.length ? series[series.length - 1] : null;
  });

  protected readonly lastTimestamp = computed(() => {
    const t = this.measurement().time;
    return t?.length ? t[t.length - 1] : null;
  });

  protected readonly showLiveness = computed(() => {
    const m = this.measurement();
    return !!m.active && !!m.measurementType && m.measurementType in MEASUREMENT_TYPE_DISPLAY_CONFIG;
  });

  protected readonly measurementType = computed(() => this.measurement().measurementType ?? '');

  protected readonly deviceName = computed(() => {
    const device = this.measurement().device as any;
    return device && typeof device === 'object' ? device.name : null;
  });

  onClick(): void {
    if (this.openGesture === 'single') this.open.emit();
    else this.select.emit();
  }

  onDoubleClick(): void {
    if (this.openGesture === 'double') this.open.emit();
  }
}
